/**
 * ContextLinkingAutocomplete Component
 * Inline autocomplete for "#" (sessions) and "@" (patients) triggers
 */

import {
  type ContextLinkingPopoverType,
  type LinkedPatientContext,
  type LinkedSessionContext,
} from '@/atoms/chatAtoms';
import { Command, CommandList } from '@heidi/design-system/command';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { useEffect, useRef } from 'react';
import { PatientList, SessionList } from './ContextLinkingCommandLists';

interface ContextLinkingAutocompleteProps {
  isOpen: boolean;
  type: ContextLinkingPopoverType;
  searchQuery: string;
  onSelectSession: (session: LinkedSessionContext) => void;
  onSelectPatient: (patient: LinkedPatientContext) => void;
  onClose: () => void;
}

export const ContextLinkingAutocomplete = ({
  isOpen,
  type,
  searchQuery,
  onSelectSession,
  onSelectPatient,
  onClose,
}: ContextLinkingAutocompleteProps) => {
  const { t } = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const commandRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Capture-phase listener forwards navigation keys to cmdk (focus stays in TipTap).
  // `isForwarding` guard prevents re-intercepting the dispatched event.
  useEffect(() => {
    if (!isOpen) return;
    let isForwarding = false;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isForwarding) return;
      if (event.key === 'Enter' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        isForwarding = true;
        commandRef.current?.dispatchEvent(
          new KeyboardEvent('keydown', { key: event.key, bubbles: true, cancelable: true })
        );
        isForwarding = false;
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full start-0 z-50 mb-2 w-72 overflow-hidden rounded-lg border bg-surface shadow-lg"
    >
      <Command ref={commandRef} shouldFilter={false} className="border-none">
        <div className="border-b px-3 py-2">
          <TypographyCaption className="text-text-secondary">
            {type === 'session'
              ? t('chat.contextLinking.searchingSessions' as TranslationKey)
              : t('chat.contextLinking.searchingPatients' as TranslationKey)}
            {searchQuery && (
              <span className="ms-1 text-text-primary">&quot;{searchQuery}&quot;</span>
            )}
          </TypographyCaption>
        </div>
        <CommandList className="max-h-64">
          {type === 'session' ? (
            <SessionList
              searchQuery={searchQuery}
              onSelect={onSelectSession}
              maxItems={8}
              showLoadMore={false}
              showHeading={false}
            />
          ) : (
            <PatientList
              searchQuery={searchQuery}
              onSelect={onSelectPatient}
              maxItems={8}
              showHeading={false}
            />
          )}
        </CommandList>
      </Command>
    </div>
  );
};
